// @ts-nocheck
import type { ItemDefinition } from '../../../../../shims/appStub';
import type { ItemMetricKey } from '../../types/binding';
import type { BindingContext } from '../binding/types';
import type { ItemMetricsAdapter } from './types';
import { resolveSyncItemMetric } from './syncItemMetrics';

const EMPTY_AGGREGATES = {
  maxLastEdited: null,
  maxLastMoved: null,
  maxLastViewed: null,
  minLastEdited: null,
  minLastMoved: null,
  minLastViewed: null,
};

export function resolveItemMetricFromCache(
  metric: ItemMetricKey,
  context: BindingContext,
): unknown | undefined {
  return context.itemMetricCache?.[metric];
}

export async function resolveItemMetricsBatch(
  item: ItemDefinition,
  keys: readonly ItemMetricKey[],
  adapter: ItemMetricsAdapter | null | undefined,
  options?: {
    categoryIds?: readonly string[];
  },
): Promise<Partial<Record<ItemMetricKey, unknown>>> {
  const uniqueKeys = [...new Set(keys)];
  if (uniqueKeys.length === 0) {
    return {};
  }

  const result: Partial<Record<ItemMetricKey, unknown>> = {};
  const asyncKeys: ItemMetricKey[] = [];

  for (const metric of uniqueKeys) {
    const syncValue = resolveSyncItemMetric(metric, item);
    if (syncValue !== undefined) {
      result[metric] = syncValue;
    } else {
      asyncKeys.push(metric);
    }
  }

  if (asyncKeys.length === 0 || !adapter || item.id == null) {
    return result;
  }

  const itemId = item.id;
  const categoryIds = options?.categoryIds ?? item.categories ?? [];

  const needs = new Set(asyncKeys);
  const tasks: Array<Promise<void>> = [];

  if (needs.has('childCount')) {
    tasks.push(
      adapter.countDirectChildren(itemId).then((v) => {
        result.childCount = v;
      }),
    );
  }
  if (needs.has('containerChildCount')) {
    tasks.push(
      adapter.countContainerChildren(itemId).then((v) => {
        result.containerChildCount = v;
      }),
    );
  }
  if (needs.has('leafChildCount')) {
    tasks.push(
      adapter.countLeafChildren(itemId).then((v) => {
        result.leafChildCount = v;
      }),
    );
  }
  const descendantChildKeys: ItemMetricKey[] = [
    'descendantCount',
    'containerDescendantCount',
    'leafDescendantCount',
  ];
  if (descendantChildKeys.some((k) => needs.has(k))) {
    tasks.push(
      adapter.getDescendantChildCounts(itemId).then((counts) => {
        if (needs.has('descendantCount')) result.descendantCount = counts.total;
        if (needs.has('containerDescendantCount')) {
          result.containerDescendantCount = counts.containerCount;
        }
        if (needs.has('leafDescendantCount')) {
          result.leafDescendantCount = counts.leafCount;
        }
      }),
    );
  }
  if (needs.has('listDepth')) {
    tasks.push(
      adapter.getListDepth(itemId).then((v) => {
        result.listDepth = v;
      }),
    );
  }
  if (needs.has('graphDepth')) {
    tasks.push(
      adapter.getGraphDepth(itemId).then((v) => {
        result.graphDepth = v;
      }),
    );
  }
  if (needs.has('inEdgeCount')) {
    tasks.push(
      adapter.countIncomingEdges(itemId).then((v) => {
        result.inEdgeCount = v;
      }),
    );
  }
  if (needs.has('filledCategoryPropertyCount') || needs.has('unfilledCategoryPropertyCount')) {
    tasks.push(
      adapter.getCategoryPropertyCounts(item, categoryIds).then((counts) => {
        if (needs.has('filledCategoryPropertyCount')) {
          result.filledCategoryPropertyCount = counts.filled;
        }
        if (needs.has('unfilledCategoryPropertyCount')) {
          result.unfilledCategoryPropertyCount = counts.unfilled;
        }
      }),
    );
  }
  if (needs.has('svgBackgroundBytes')) {
    tasks.push(
      adapter.getSvgBackgroundBytes(item).then((v) => {
        result.svgBackgroundBytes = v;
      }),
    );
  }
  if (needs.has('hasCanvasThumb')) {
    tasks.push(
      adapter.hasCanvasThumb(itemId).then((v) => {
        result.hasCanvasThumb = v;
      }),
    );
  }

  const descendantKeys: ItemMetricKey[] = [
    'descendantsMaxLastEdited',
    'descendantsMaxLastMoved',
    'descendantsMaxLastViewed',
    'descendantsMinLastEdited',
    'descendantsMinLastMoved',
    'descendantsMinLastViewed',
  ];
  if (descendantKeys.some((k) => needs.has(k))) {
    tasks.push(
      adapter.getDescendantTimestampAggregates(itemId).then((agg) => {
        if (needs.has('descendantsMaxLastEdited')) result.descendantsMaxLastEdited = agg.maxLastEdited;
        if (needs.has('descendantsMaxLastMoved')) result.descendantsMaxLastMoved = agg.maxLastMoved;
        if (needs.has('descendantsMaxLastViewed')) result.descendantsMaxLastViewed = agg.maxLastViewed;
        if (needs.has('descendantsMinLastEdited')) result.descendantsMinLastEdited = agg.minLastEdited;
        if (needs.has('descendantsMinLastMoved')) result.descendantsMinLastMoved = agg.minLastMoved;
        if (needs.has('descendantsMinLastViewed')) result.descendantsMinLastViewed = agg.minLastViewed;
      }),
    );
  }

  await Promise.all(tasks);
  return result;
}

/** Populates sync-resolvable metrics from the item record (no adapter). */
export function buildSyncItemMetricCache(
  item: ItemDefinition,
  keys: readonly ItemMetricKey[],
): Partial<Record<ItemMetricKey, unknown>> {
  const result: Partial<Record<ItemMetricKey, unknown>> = {};
  for (const metric of [...new Set(keys)]) {
    const value = resolveSyncItemMetric(metric, item);
    if (value !== undefined) {
      result[metric] = value;
    }
  }
  return result;
}

export function createEmptyDescendantAggregates() {
  return { ...EMPTY_AGGREGATES };
}
